import { parseISO } from 'date-fns';
import { ActivationDateType, ExpirationDateType, Package } from 'src/models/package/_types';

export interface PackageDefaultDates {
  activationDate: Date;
  expiresOn: Date | null;
}

export const deriveActivationDateType = (
  pkg?: Partial<Package>
): ActivationDateType | null => {
  if (pkg?.activationDateType) {
    return pkg.activationDateType as ActivationDateType;
  }
  if (pkg?.allowActivationDate) {
    return 'client_chooses';
  }
  if (pkg?.activatesInDays) {
    return 'offset';
  }
  if (pkg?.presetActivationDate) {
    return 'fixed_date';
  }

  return null;
};

export const deriveExpirationDateType = (
  pkg?: Partial<Package>
): ExpirationDateType | null => {
  if (pkg?.expirationDateType) {
    return pkg.expirationDateType as ExpirationDateType;
  }
  if (pkg?.expiresInDays !== '' && pkg?.expiresInDays != null) {
    return 'offset';
  }
  if (pkg?.presetExpirationDate) {
    return 'fixed_date';
  }

  return null;
};

export const hasDelayedActivation = (pkg?: Partial<Package>): boolean => {
  const activationType = deriveActivationDateType(pkg);

  return (
    activationType === 'client_chooses' ||
    activationType === 'fixed_date' ||
    (activationType === 'offset' && !!pkg?.activatesInDays)
  );
};

const toDate = (value?: Date | string | null): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  return parseISO(value);
};

export const resolvePackageActivationDate = (
  pkg: Partial<Package>,
  purchaseDate: Date = new Date(),
  override?: Date | null
): Date => {
  if (override) {
    return override;
  }

  const activationType = deriveActivationDateType(pkg);

  switch (activationType) {
    case 'fixed_date': {
      const preset = toDate(pkg.presetActivationDate);
      return preset || purchaseDate;
    }
    case 'offset':
      if (pkg.activatesInDays) {
        const date = new Date(purchaseDate);
        date.setDate(date.getDate() + Number(pkg.activatesInDays));
        return date;
      }
      return purchaseDate;
    case 'client_chooses':
    default:
      return purchaseDate;
  }
};

export const resolvePackageExpirationDate = (
  pkg: Partial<Package>,
  activationDate: Date,
  override?: Date | null
): Date | null => {
  if (override) {
    return override;
  }

  const expirationType = deriveExpirationDateType(pkg);

  switch (expirationType) {
    case 'fixed_date':
      return toDate(pkg.presetExpirationDate);
    case 'offset':
      if (pkg.expiresInDays !== '' && pkg.expiresInDays != null) {
        const date = new Date(activationDate);
        date.setDate(date.getDate() + Number(pkg.expiresInDays));
        return date;
      }
      return null;
    default:
      return null;
  }
};

export const getPackageDefaultDates = (
  pkg: Partial<Package>,
  purchaseDate: Date = new Date()
): PackageDefaultDates => {
  const activationDate = resolvePackageActivationDate(pkg, purchaseDate);

  return {
    activationDate,
    expiresOn: resolvePackageExpirationDate(pkg, activationDate),
  };
};
